import useSWR from "swr";
import qs from "qs";
import { AmplifyGet } from "@sunrun/sales-experience-shared/amplify-api-wrapper";
import { orderBy } from "lodash";
import {
  AsyncRequestIdResponse,
  SiteSurveySlotsResponse,
  SiteSurveyAppointment,
} from "../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { subscribeToAsyncRequest } from "utils/subscribeToAsyncRequest";
import { useAuthToken } from "hooks/useAuthToken";
import { getWithAuth } from "utils/dataUtils";
import { logger } from "providers/logger";

type SiteSurveySlot = {
  appointmentId: string;
  gradeRating: number;
  arrivalEndDate: string;
  arrivalStartDate: string;
  startTime: string;
  endTime: string;
};

const useSiteSurveySlots = (prospectId: string, appointmentId: string) => {
  const SITE_SURVEY_SLOTS_URL = `/prospect/${prospectId}/site-survey/slot/`;
  const authToken = useAuthToken("HYBRID");
  const { data, error, isLoading, mutate } = useSWR(
    [SITE_SURVEY_SLOTS_URL, authToken, appointmentId],
    async ([url, authToken, appointmentId]) => {
      if (!authToken || !appointmentId) return;
      const resp = (await AmplifyGet(
        "Get Site Survey Slots",
        "OfferExpApi",
        `${url}?${qs.stringify({
          appointmentId,
          isComplexAppointment: false,
        })}`,
        {
          headers: {
            Authorization: `Bearer ${authToken.token}`,
            "X-Auth-Type": authToken.type,
          },
        },
        logger
      )) as AsyncRequestIdResponse;
      try {
        const siteSurveySlots = (await subscribeToAsyncRequest(
          authToken.token,
          resp.asyncRequestId
        )) as SiteSurveySlotsResponse;
        return orderBy(
          siteSurveySlots,
          "startTime",
          "asc"
        ) as Array<SiteSurveySlot>;
      } catch (err: unknown) {
        const message =
          typeof err === "string"
            ? err
            : err instanceof Error
            ? err.message
            : "";
        const isEmptySlots = /no\s*(available)?\s*(slot|appointment)/i.test(
          message
        );
        if (isEmptySlots) {
          return [];
        }
        throw err;
      }
    },
    {
      dedupingInterval: 1000 * 60 * 10,
      errorRetryCount: 1,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnMount: true,
      revalidateOnReconnect: false,
      keepPreviousData: false,
      shouldRetryOnError: false,
    }
  );

  return {
    data,
    isLoading,
    error,
    retry: mutate,
  } as const;
};

type UseSiteSurveyAppointmentsOptions = {
  specialWorktype?: string;
};

const SCHEDULED_SITE_SURVEY_APPOINTMENT_STATUSES = [
  "Scheduled",
  "Dispatched",
  "In Progress",
  "Complete - Interior",
  "Complete - Exterior",
  "Complete",
] as const;

const isScheduledSiteSurveyAppointmentStatus = (
  status: SiteSurveyAppointment["status"] | string
): boolean =>
  (SCHEDULED_SITE_SURVEY_APPOINTMENT_STATUSES as readonly string[]).includes(
    status
  );

const isScheduledSiteSurveyAppointment = (
  value: unknown
): value is SiteSurveyAppointment =>
  typeof value === "object" &&
  value !== null &&
  "status" in value &&
  isScheduledSiteSurveyAppointmentStatus(
    (value as SiteSurveyAppointment).status
  );

const buildScheduledSiteSurveyAppointmentFromBookedSlot = (
  slot: SiteSurveySlot,
  readyAppointment: SiteSurveyAppointment | undefined,
  prospectId: string
): SiteSurveyAppointment => {
  if (readyAppointment) {
    return {
      ...readyAppointment,
      arrivalStartDate: slot.arrivalStartDate,
      arrivalEndDate: slot.arrivalEndDate,
      status: "Scheduled",
    };
  }

  return {
    address: "",
    appointmentId: slot.appointmentId,
    appointmentNumber: "",
    appointmentType: "Site Survey",
    arrivalStartDate: slot.arrivalStartDate,
    arrivalEndDate: slot.arrivalEndDate,
    cancellable: false,
    cancellationReasons: [],
    customSchedule: false,
    email: "",
    flexibility: false,
    form: false,
    formVersion: 0,
    homeownerName: "",
    opportunityId: "",
    phone: "",
    prospectId,
    reschedulable: true,
    rescheduleReasons: ["Other"],
    status: "Scheduled",
  };
};

const mergeBookedSiteSurveyAppointmentIntoCache = (
  current: SiteSurveyAppointment[] | undefined,
  bookedAppointment: SiteSurveyAppointment
): SiteSurveyAppointment[] => {
  const rows = Array.isArray(current) ? current : [];
  const withoutBookedRow = rows.filter(
    (appointment) =>
      appointment.appointmentId !== bookedAppointment.appointmentId &&
      !isScheduledSiteSurveyAppointmentStatus(appointment.status)
  );

  return [...withoutBookedRow, bookedAppointment];
};

const useSiteSurveyAppointments = (
  prospectId: string | undefined,
  options?: UseSiteSurveyAppointmentsOptions
) => {
  const authToken = useAuthToken("HYBRID");
  const basePath = `/prospect/${prospectId}/site-survey/site-survey-appointments`;
  const specialWorktype = options?.specialWorktype;
  const url =
    specialWorktype != null && specialWorktype !== ""
      ? `${basePath}?${qs.stringify({ specialWorktype })}`
      : basePath;
  const { data, isLoading, error, mutate, isValidating } = useSWR(
    [
      "Get Site Survey Appointments",
      prospectId ? url : undefined,
      authToken?.token,
    ],
    getWithAuth<SiteSurveyAppointment[]>
  );

  // Gateway can return HTTP 200 + `{ message: string }` instead of an array; never call `.find` on that.
  const rows = Array.isArray(data) ? data : [];
  if (
    process.env.NODE_ENV !== "production" &&
    data !== undefined &&
    !Array.isArray(data)
  ) {
    console.warn("[SiteSurveyAppointments] expected array, got", data);
  }

  const siteSurveyScheduled = rows.find((appt: SiteSurveyAppointment) =>
    isScheduledSiteSurveyAppointmentStatus(appt.status)
  );

  const readyToBeScheduled = rows.find(
    (appt: SiteSurveyAppointment) => appt.status === "Ready To Be Scheduled"
  );
  return {
    siteSurveyReadyToBeScheduled: readyToBeScheduled,
    siteSurveyAppointment: siteSurveyScheduled,
    siteSurveyAppointmentsLoading: isLoading || isValidating,
    siteSurveyApointmentError: error,
    mutateSiteSurveyAppointments: mutate,
  };
};

export {
  useSiteSurveySlots,
  useSiteSurveyAppointments,
  buildScheduledSiteSurveyAppointmentFromBookedSlot,
  mergeBookedSiteSurveyAppointmentIntoCache,
  isScheduledSiteSurveyAppointment,
  isScheduledSiteSurveyAppointmentStatus,
  SCHEDULED_SITE_SURVEY_APPOINTMENT_STATUSES,
};
export type { SiteSurveySlot, UseSiteSurveyAppointmentsOptions };
